/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RecordingManagementFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Recording Management form component with file upload and AI TTS generation.
    Supports both manual file upload and AI-generated recordings.

 🔧 Notes:
    - Implements file upload with validation
    - Supports AI TTS generation using AI profiles
    - Handles domain-based recording management
    - Provides comprehensive validation and error handling
    - Integrates with FormBox for consistent UI

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Mic, Upload, Bot } from 'lucide-react';

import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Select from '../../components/ui/select';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { RECORDING_CATEGORIES, SUPPORTED_LANGUAGES } from './types';
import {
  useRecordingForm,
  useRecordingData,
  useTtsGeneration,
  useSingleFileUpload,
} from './hooks';
import {
  createRecordingFromFile,
  createRecordingFromTts,
  updateRecording,
  deleteRecording,
} from './api';
import { VoiceRecording } from './types';
import { RecordingFileUploadSection } from './components/RecordingFileUploadSection';
import { RecordingTtsSection } from './components/RecordingTtsSection';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { RecordingSystemModeSection } from './components/RecordingSystemModeSection';
import { LibraryBig } from 'lucide-react';

export default function RecordingManagementFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo, notifications, removeNotification } = useNotifications();

  // Use hooks
  const form = useRecordingForm();
  const data = useRecordingData(id);
  const tts = useTtsGeneration();
  const fileUpload = useSingleFileUpload();

  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [startSilenceMs, setStartSilenceMs] = useState(50);
  const [endSilenceMs, setEndSilenceMs] = useState(100);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Reset form when navigating to 'new' after Save and New (must run BEFORE initialization)
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Reset when:
    // 1. We're navigating to 'new' (id is 'new' or undefined) AND
    // 2. Either the ID changed from a number to 'new', OR there's a _reset param that changed
    const isNavigatingToNew = (!id || id === 'new');
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
    const resetParamChanged = currentResetParam && currentResetParam !== lastResetParamRef.current;

    const shouldReset = isNavigatingToNew && (idChangedToNew || resetParamChanged);

    if (shouldReset) {
      // Reset all form state
      form.resetForm();
      fileUpload.removeFile();
      tts.clearPreview();

      // Clean up audio URL if it exists
      if (data.audioUrl) {
        URL.revokeObjectURL(data.audioUrl);
      }
      data.setAudioUrl(null);

      form.initialSnapshotRef.current = null;

      // Set default AI profile for new recordings
      if (data.aiProfiles.length > 0) {
        form.setSelectedAiProfile(data.aiProfiles[0]);
      }

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, searchParams, form, fileUpload, tts, data]);

  // Initialize form data from loaded recording (must run AFTER reset)
  useEffect(() => {
    // Only initialize if we're NOT in a reset scenario
    const currentResetParam = searchParams.get('_reset');
    const isResetting = (!id || id === 'new') && currentResetParam;

    if (isResetting) {
      // Don't initialize if we're resetting
      return;
    }

    if (data.recording && data.isEditMode) {
      form.setFormData(data.recording);

      if (data.recording.is_ai_generated) {
        form.setRecordingMode('tts');
        form.setTtsText(data.recording.tts_text || '');
        setStartSilenceMs(data.recording.start_silence_ms ?? 50);
        setEndSilenceMs(data.recording.end_silence_ms ?? 100);
        const profile = data.aiProfiles.find((p) => String(p.id) === String(data.recording.ai_profile_id));
        form.setSelectedAiProfile(profile || null);
      } else if (data.recording.file_path?.includes('/sounds/')) {
        form.setRecordingMode('system');
        form.setFormData({
            ...data.recording,
            voice_guide_id: -1, // Dummy ID since we don't store it in the DB to represent loaded state
            voice_guide_file_path: data.recording.filename
        });
      } else {
        // Set mode to 'upload' for non-AI generated recordings
        form.setRecordingMode('upload');
      }

      form.initialSnapshotRef.current = form.buildSnapshot();
    } else if (!id || id === 'new') {
      // Set default AI profile for new recordings
      if (data.aiProfiles.length > 0 && !form.selectedAiProfile) {
        form.setSelectedAiProfile(data.aiProfiles[0]);
      }
      form.initialSnapshotRef.current = form.buildSnapshot();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.recording, data.isEditMode, data.aiProfiles, id]);

  // Determine audio URL (from file preview, TTS preview, or loaded recording)
  const audioUrl = fileUpload.filePreview || tts.ttsPreview || data.audioUrl;

  // Update audio URL when file or TTS preview changes
  useEffect(() => {
    if (fileUpload.filePreview) {
      data.setAudioUrl(fileUpload.filePreview);
    } else if (tts.ttsPreview) {
      data.setAudioUrl(tts.ttsPreview);
    }
  }, [fileUpload.filePreview, tts.ttsPreview, data]);

  // Form submission
  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<VoiceRecording | undefined> => {
      e?.preventDefault();

      if (!form.formData.name?.trim()) {
        showError(t('recordingManagement.errors.nameRequired'));
        return undefined;
      }

      if (form.recordingMode === 'upload' && !fileUpload.selectedFile && !data.isEditMode) {
        showError(t('recordingManagement.errors.fileRequired'));
        return undefined;
      }

      if (form.recordingMode === 'tts' && !form.ttsText.trim()) {
        showError(t('recordingManagement.errors.textRequired'));
        return undefined;
      }

      if (form.recordingMode === 'system' && !form.buildSnapshot().voiceGuideId) {
        showError(t('recordingManagement:errors.voiceGuideRequired') || 'A Voice Guide must be selected.');
        return undefined;
      }

      try {
        setIsSaving(true);

        let result: VoiceRecording;

        if (data.isEditMode && id) {
          // Update existing recording
          result = await updateRecording(id, {
            ...form.formData,
            file: fileUpload.selectedFile || undefined,
            text: form.recordingMode === 'tts' ? form.ttsText : undefined,
            ai_profile_id: form.recordingMode === 'tts' ? form.selectedAiProfile?.id : undefined,
            start_silence_ms: form.recordingMode === 'tts' ? startSilenceMs : undefined,
            end_silence_ms: form.recordingMode === 'tts' ? endSilenceMs : undefined,
            voice_guide_id: (form.recordingMode === 'system' && form.buildSnapshot().voiceGuideId !== -1) ? form.buildSnapshot().voiceGuideId : undefined,
            voice_guide_file_path: (form.recordingMode === 'system' && form.buildSnapshot().voiceGuideId !== -1) ? form.buildSnapshot().voiceGuideFilePath : undefined,
          } as any);
        } else {
          // Create new recording
          if (form.recordingMode === 'upload' && fileUpload.selectedFile) {
            result = await createRecordingFromFile({
              name: form.formData.name!,
              description: form.formData.description,
              category: form.formData.category!,
              language: form.formData.language!,
              file: fileUpload.selectedFile,
            });
          } else if (form.recordingMode === 'tts' && form.selectedAiProfile) {
            result = await createRecordingFromTts({
              name: form.formData.name!,
              description: form.formData.description,
              category: form.formData.category!,
              language: form.formData.language!,
              text: form.ttsText,
              ai_profile_id: form.selectedAiProfile.id,
              start_silence_ms: startSilenceMs,
              end_silence_ms: endSilenceMs,
            });
          } else if (form.recordingMode === 'system') {
            const systemData = {
              name: form.formData.name!,
              description: form.formData.description,
              category: form.formData.category!,
              language: form.formData.language!,
              voice_guide_id: form.buildSnapshot().voiceGuideId!,
              voice_guide_file_path: form.buildSnapshot().voiceGuideFilePath!
            };
            // Dynamic import or destructuring to avoid big changes at the top level
            const apiModule = await import('./api');
            if (apiModule.createRecordingFromSystem) {
                result = await apiModule.createRecordingFromSystem(systemData);
            } else {
                throw new Error('createRecordingFromSystem missing');
            }
          } else {
            throw new Error('Invalid recording mode or missing data');
          }
        }

        showSuccess(t('recordingManagement.messages.saveSuccess'));
        return result;
      } catch (error) {
        showError(error.message || t('recordingManagement.errors.saveFailed'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [
      form,
      fileUpload.selectedFile,
      data.isEditMode,
      id,
      startSilenceMs,
      endSilenceMs,
      showSuccess,
      showError,
      showInfo,
      t,
    ],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && data.isEditMode && id) {
      // Return current data with ID for navigation purposes
      return { id: id as any, uuid: id } as VoiceRecording;
    }

    return savedData;
  }, [handleSubmit, data.isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/voice-prompts/recording',
    newPath: '/settings/voice-prompts/recording/new',
    mode: data.isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Recording Management uses UUID for navigation
      const idToUse = savedData.uuid ?? savedData.id;
      return `/settings/voice-prompts/recording/${idToUse}`;
    },
    getFormData: () => ({
      formData: form.formData
    }),
    storageKey: 'recordingManagement:saveAndNewData',
  });

  const handleCancel = useCallback(() => {
    // Cancel should reset the form to original values without leaving
    if (data.isEditMode && id) {
      // Reload the recording data
      data.loadAiProfiles().then((profiles) => {
        if (id) {
          data.loadRecording(id, profiles);
        }
      });
    } else {
      // Reset form to initial state for create mode
      form.resetForm();
      fileUpload.removeFile();
      tts.clearPreview();
      data.setAudioUrl(null);
    }
  }, [data, id, form, fileUpload, tts]);

  const handleDeleteClick = useCallback(() => {
    if (!data.isEditMode || !id) {
      return;
    }
    setShowDeleteModal(true);
  }, [data.isEditMode, id]);

  const confirmDelete = useCallback(async () => {
    if (!id) return;
    setShowDeleteModal(false);

    try {
      setIsSaving(true);
      await deleteRecording(id);
      showSuccess(t('recordingManagement.messages.deleteSuccess'));
      navigate('/settings/voice-prompts/recording');
    } catch (error) {
      showError(error.message || t('recordingManagement.errors.deleteFailed'));
    } finally {
      setIsSaving(false);
    }
  }, [id, showSuccess, showError, t, navigate]);

  if (data.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
      icon={<Mic className="h-5 w-5 text-primary" />}
      title={data.isEditMode ? `${t('recordingManagement.editTitle')} - ${form.formData.name || ''}` : t('recordingManagement.createTitle')}
      backButtonText={t('common.list')}
      onBack={() => navigate('/settings/voice-prompts/recording')}
    />

      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information */}
            <FormBox title={t('recordingManagement.sections.basicInfo')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {/* Row 1: Name | Input | Description | Input */}
                <Label htmlFor="name" required className="text-left">
                  {t('recordingManagement.fields.name.label')}
                </Label>
                <Input
                  id="name"
                  value={form.formData.name || ''}
                  onChange={(e) => form.handleInputChange('name', e.target.value)}
                  placeholder={t('recordingManagement.fields.name.placeholder')}
                  required
                />
                <Label htmlFor="description" className="text-left">
                  {t('recordingManagement.fields.description.label')}
                </Label>
                <Input
                  id="description"
                  value={form.formData.description || ''}
                  onChange={(e) => form.handleInputChange('description', e.target.value)}
                  placeholder={t('recordingManagement.fields.description.placeholder')}
                />
              </div>
            </FormBox>

            {/* Recording Mode Selection — only shown in create mode */}
            {!data.isEditMode && (
            <FormBox title={t('recordingManagement.sections.recordingMode')}>
              <div className="flex gap-4">
                  <>
                    <Button
                      type="button"
                      variant={form.recordingMode === 'upload' ? 'default' : 'outline'}
                      onClick={() => form.setRecordingMode('upload')}
                      className="flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {t('recordingManagement.modes.upload')}
                    </Button>
                    <Button
                      type="button"
                      variant={form.recordingMode === 'tts' ? 'default' : 'outline'}
                      onClick={() => form.setRecordingMode('tts')}
                      className="flex items-center gap-2"
                    >
                      <Bot className="h-4 w-4" />
                      {t('recordingManagement.modes.aiTts')}
                    </Button>
                    <Button
                      type="button"
                      variant={form.recordingMode === 'system' ? 'default' : 'outline'}
                      onClick={() => form.setRecordingMode('system')}
                      className="flex items-center gap-2"
                    >
                      <LibraryBig className="h-4 w-4" />
                      {t('recordingManagement.modes.system') || 'Native Voice Guide'}
                    </Button>
                  </>
              </div>
            </FormBox>
            )}

            {/* File Upload Mode */}
            {form.recordingMode === 'upload' && (
              <RecordingFileUploadSection
                fileUpload={fileUpload}
                data={data}
                onPlayError={() => showError(t('recordingManagement.errors.playFailed'))}
                t={t}
              />
            )}

            {/* System / PBX Voice Guide Mode */}
            {form.recordingMode === 'system' && (
              <RecordingSystemModeSection
                formData={form.formData as any}
                setFormData={form.setFormData as any}
                errors={{}}
                clearError={() => {}}
              />
            )}

            {/* AI TTS Mode */}
            {form.recordingMode === 'tts' && (
              <RecordingTtsSection
                aiProfiles={data.aiProfiles}
                selectedAiProfile={form.selectedAiProfile}
                onProfileChange={form.setSelectedAiProfile}
                ttsText={form.ttsText}
                onTtsTextChange={form.setTtsText}
                startSilenceMs={startSilenceMs}
                endSilenceMs={endSilenceMs}
                onStartSilenceChange={setStartSilenceMs}
                onEndSilenceChange={setEndSilenceMs}
                audioUrl={audioUrl}
                onSetAudioUrl={(url) => data.setAudioUrl(url)}
                isEditMode={data.isEditMode}
                tts={tts}
                recordingName={form.formData.name || ''}
                onPlayError={() => showError(t('recordingManagement.errors.playFailed'))}
                t={t}
              />
            )}
          </div>

          <FixedActionBar
            onDelete={handleDeleteClick}
            onCancel={handleCancel}
            onSaveWithAction={handleSaveWithAction}
            isSaving={isSaving}
            isEditMode={data.isEditMode}
            hasChanges={form.hasChanges}
            saveText={t('common.save')}
            cancelText={t('common.cancel')}
            deleteText={t('common.delete')}
            useSaveDropdown={true}
            saveDropdownStorageKey="recordingManagement:saveActionPreference"
          />
        </form>
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('common.confirmDelete') || 'Confirm Deletion'}
        message={t('recordingManagement.confirmDelete') || 'Are you sure you want to delete this recording?'}
        confirmText={t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="danger"
      />
    </div>
  );
}
